import { useCallback, useEffect, useRef, useState } from "react";
import { classifyRequestStatus, getFuelPlanRequestStatus } from "../lib/fuelPlanApi";
import { ApiError, isAbortError } from "../lib/api";

/**
 * Generic row shape the watcher needs — both `FuelPlanSummary` (plans history)
 * and `HeroFuelItem` (dashboard) satisfy it because they both carry a discriminant
 * `kind`, a `status` string, and a `requestId`. The hook is row-shape-agnostic
 * so callers can hand it whatever they already render.
 */
export type WatchableRow =
  | { kind: "request"; requestId: string; status: string }
  | { kind: "plan"; requestId: string; status: string };

export type PendingPlanResolution = {
  requestId: string;
  /**
   * Terminal result for a watched request. The watcher ALWAYS emits one of
   * these so the UI can clear its in-flight spinner + surface a message —
   * never leaving a box spinning forever (Contract A):
   *   - `completed` — the plan built AND is still live; `fuelPlanId` is set.
   *   - `superseded` — the plan built but was replaced by a newer regenerate
   *     before this watcher saw it (expired/deleted). Terminal — the user's
   *     latest plans carry the real result.
   *   - `failed`/`expired` — the server marked the request unbuildable.
   *     (`not_found`-style repeated 404s also land here — a scoped 404 won't
   *     heal by polling.)
   *   - `timeout` — the poll gave up after {@link POLL_TIMEOUT_MS}; the job may
   *     yet finish server-side (we emailed the user) but the UI stops waiting.
   *   - `auth_blocked` — the poll hit 401/403 (session lapse / preview gate);
   *     polling stops and the UI asks the user to refresh.
   */
  result: "completed" | "superseded" | "failed" | "expired" | "timeout" | "auth_blocked";
  /** Set when result === "completed" (and sometimes on `superseded`). */
  fuelPlanId: string | null;
  /** Curated server-side failure copy — set only when result === "failed". */
  failureSummary?: string | null;
};

/**
 * Backoff schedule for the request-status poll. Same shape as PlanPending:
 * fast at first (most plans finish in 30–60s) then ease off. Capped at 10s so
 * a multi-minute build doesn't hammer the API.
 */
const POLL_BACKOFF_MS = [3000, 5000, 8000, 10000, 15000] as const;
function nextPollInterval(attemptIndex: number): number {
  return POLL_BACKOFF_MS[Math.min(attemptIndex, POLL_BACKOFF_MS.length - 1)];
}

/**
 * Poll cadence while the tab is hidden. We deliberately keep polling (slowly)
 * rather than pausing: the app-root PlanGenerationProvider is the publisher
 * for cross-tab notifications (BroadcastChannel / storefront listener), and a
 * hidden tab that stops polling can never publish "your plan is ready" to the
 * visible storefront tab the user is actually looking at.
 */
const HIDDEN_POLL_MS = 60_000;

/** Consecutive scoped 404s before we stop polling a request — a 404 means the
 *  row is gone or belongs to another principal; more polling won't heal it. */
const NOT_FOUND_STRIKE_LIMIT = 3;

/**
 * Stop polling any single request after this long — protects against runaways.
 * 10 min so a build that hits a server-side RETRY (which restarts the clock and
 * can push total time past a single build's ~4-min window) still resolves in
 * the UI rather than timing out mid-retry. The backoff eases to 15s at the tail
 * so a long wait doesn't hammer the status endpoint.
 */
const POLL_TIMEOUT_MS = 10 * 60 * 1000;

/** How long a `requestId` stays in `justResolved` so the row keeps flashing. */
const FLASH_DURATION_MS = 3000;

/**
 * Lightweight background poll for fuel-plan requests that are still building.
 *
 * Given the rows a screen is already rendering, the hook extracts the
 * `processing`/`requested` request IDs, polls each one on a sensible backoff,
 * and calls `onResolve` once any of them lands in a terminal state. Screens
 * use the callback to refetch their own data (so the row reappears as a
 * completed plan) and read `justResolved.has(requestId)` to apply a brief
 * flash class to the row.
 *
 * Pauses while the tab is hidden (no point polling for a user who's looking
 * at a different tab) and resumes on visibility change. Aborts in-flight
 * fetches on unmount / row removal.
 */
export function useGeneratingPlanWatcher(
  rows: ReadonlyArray<WatchableRow>,
  onResolve: (info: PendingPlanResolution) => void
): {
  /** Request IDs whose request has resolved within the last few seconds. */
  justResolved: ReadonlySet<string>;
  /** True when at least one row is still in flight (poll loop active). */
  hasPending: boolean;
  /**
   * Re-arm the foreground poll for a `requestId` the watcher previously
   * resolved/gave up on (CT-3 / C3). Clears that id's internal
   * `resolvedIds`/`startedAt`/`attempts` and bumps a monotonic generation so
   * the poll effect re-registers and starts a fresh clock for it. Used when a
   * retry hands back the SAME still-processing requestId after a foreground
   * `timeout` — the row is already in `pendingIds`, so `track()` alone can't
   * restart the poll. Does NOT emit any resolution or toast.
   */
  resetWatch: (requestId: string) => void;
} {
  // Stable handle to the latest callback so we never re-poll on caller renders.
  const onResolveRef = useRef(onResolve);
  onResolveRef.current = onResolve;

  const [justResolved, setJustResolved] = useState<ReadonlySet<string>>(() => new Set());

  // ── Re-arm plumbing (CT-3) ───────────────────────────────────────────────
  // A monotonic counter bumped by `resetWatch`; folding it into the effect dep
  // key forces the poll effect to re-register (fresh startedAt/attempts) even
  // when the set of pending ids is unchanged. `rearmedIdsRef` carries which ids
  // to re-arm across the re-register so the effect can prime just those.
  const [watchGeneration, setWatchGeneration] = useState(0);
  const rearmedIdsRef = useRef<Set<string>>(new Set());
  const resetWatch = useCallback((requestId: string) => {
    if (!requestId) return;
    rearmedIdsRef.current.add(requestId);
    setWatchGeneration((g) => g + 1);
  }, []);

  // Pending request IDs derived from the rows. Plan rows are skipped — once
  // they've materialized they're no longer "generating", even if their internal
  // `status` field happens to read `processing` (legacy shape).
  const pendingIds = rowsToPendingIds(rows);

  // Stable string token — re-running the effect only when the *set* of pending
  // ids changes, not when the rows array identity changes every render.
  const pendingKey = [...pendingIds].sort().join(",");

  // Track in-flight aborts and flash-clear timers across renders so the
  // cleanup path can cancel them without re-reading from React state.
  const flashTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const hasPending = pendingIds.size > 0;

  const markResolved = useCallback((info: PendingPlanResolution) => {
    setJustResolved((curr) => {
      if (curr.has(info.requestId)) return curr;
      const next = new Set(curr);
      next.add(info.requestId);
      return next;
    });
    // Schedule the flash class to clear after a short window.
    const existing = flashTimersRef.current.get(info.requestId);
    if (existing) clearTimeout(existing);
    const timer = setTimeout(() => {
      flashTimersRef.current.delete(info.requestId);
      setJustResolved((curr) => {
        if (!curr.has(info.requestId)) return curr;
        const next = new Set(curr);
        next.delete(info.requestId);
        return next;
      });
    }, FLASH_DURATION_MS);
    flashTimersRef.current.set(info.requestId, timer);
    onResolveRef.current(info);
  }, []);

  useEffect(() => {
    if (pendingIds.size === 0) return;

    let cancelled = false;
    const timers = new Map<string, ReturnType<typeof setTimeout>>();
    const abortControllers = new Map<string, AbortController>();
    const startedAt = new Map<string, number>();
    const attempts = new Map<string, number>();
    const notFoundStrikes = new Map<string, number>();
    // Requests that hit a terminal state — visibility-resume must not
    // reschedule them (markResolved dedupes, but re-polling wastes calls).
    const resolvedIds = new Set<string>();
    for (const id of pendingIds) startedAt.set(id, Date.now());
    // Consume any pending re-arm requests: this effect re-registered because
    // `resetWatch` bumped `watchGeneration`, giving the id a fresh clock above.
    rearmedIdsRef.current.clear();

    function resolveAndStop(info: PendingPlanResolution) {
      resolvedIds.add(info.requestId);
      markResolved(info);
    }

    function schedule(requestId: string, delayMs: number) {
      if (cancelled) return;
      const existing = timers.get(requestId);
      if (existing) clearTimeout(existing);
      const timer = setTimeout(() => {
        timers.delete(requestId);
        void pollOnce(requestId);
      }, delayMs);
      timers.set(requestId, timer);
    }

    /** Backoff for the next tick — slow (fixed) while the tab is hidden. */
    function nextDelay(requestId: string): number {
      const attempt = attempts.get(requestId) ?? 0;
      attempts.set(requestId, attempt + 1);
      const base = nextPollInterval(attempt);
      if (typeof document !== "undefined" && document.hidden) {
        return Math.max(base, HIDDEN_POLL_MS);
      }
      return base;
    }

    async function pollOnce(requestId: string) {
      if (cancelled || resolvedIds.has(requestId)) return;
      const startedTs = startedAt.get(requestId) ?? Date.now();
      if (Date.now() - startedTs > POLL_TIMEOUT_MS) {
        // Stop watching this request — a stuck job won't resolve via polling.
        // Emit a terminal `timeout` signal so the UI clears its spinner + tells
        // the user (Contract A); the job may still finish server-side (we
        // emailed them) and the screen's next refresh / nav picks up the real
        // terminal state.
        resolveAndStop({ requestId, result: "timeout", fuelPlanId: null });
        return;
      }
      const ctrl = new AbortController();
      abortControllers.set(requestId, ctrl);
      try {
        const status = await getFuelPlanRequestStatus(requestId, ctrl.signal);
        abortControllers.delete(requestId);
        if (cancelled || ctrl.signal.aborted) return;
        notFoundStrikes.delete(requestId);
        const cls = classifyRequestStatus(status);
        if (cls.kind === "live") {
          resolveAndStop({ requestId, result: "completed", fuelPlanId: cls.fuelPlanId });
          return;
        }
        if (cls.kind === "superseded") {
          // The request completed but its plan was replaced (expired/deleted)
          // by a newer regenerate before this watcher saw it. Terminal —
          // keeping polling here was the "spinner never resolves" bug.
          resolveAndStop({ requestId, result: "superseded", fuelPlanId: cls.fuelPlanId });
          return;
        }
        if (cls.kind === "failed" || cls.kind === "expired") {
          resolveAndStop({
            requestId,
            result: cls.kind,
            fuelPlanId: null,
            failureSummary: cls.kind === "failed" ? cls.failureSummary : null
          });
          return;
        }
        // Still processing/requested — keep polling.
        schedule(requestId, nextDelay(requestId));
      } catch (err) {
        abortControllers.delete(requestId);
        if (cancelled || isAbortError(err)) return;
        // Auth blocked — stop polling this id; the user has to re-auth to
        // fix it and we don't want a hot loop of 401s in the background.
        // This also covers a mid-session preview-gate flip (403
        // `preview_access_required`): the background poll goes quiet and the
        // user hits the full gate page on their next navigation.
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
          // Emit ONCE so the UI clears the spinner + prompts a refresh, then
          // stop polling (markResolved dedupes on requestId, so even if the
          // effect re-ran it wouldn't double-fire).
          resolveAndStop({ requestId, result: "auth_blocked", fuelPlanId: null });
          return;
        }
        // Scoped 404 — the request row is gone (or belongs to another
        // principal). It won't heal by polling; after a few strikes treat it
        // as failed so the UI clears its spinner (Contract A).
        if (err instanceof ApiError && err.status === 404) {
          const strikes = (notFoundStrikes.get(requestId) ?? 0) + 1;
          notFoundStrikes.set(requestId, strikes);
          if (strikes >= NOT_FOUND_STRIKE_LIMIT) {
            resolveAndStop({ requestId, result: "failed", fuelPlanId: null });
            return;
          }
        }
        // Transient network failure — back off and try again.
        schedule(requestId, nextDelay(requestId));
      }
    }

    // Kick off the first poll for each pending request immediately, then let
    // pollOnce schedule its own next tick on backoff.
    for (const id of pendingIds) {
      void pollOnce(id);
    }

    // Resume polling promptly when the tab regains focus — cut short the slow
    // hidden-tab cadence rather than waiting out the current interval.
    function onVisibility() {
      if (cancelled) return;
      if (typeof document === "undefined" || document.hidden) return;
      for (const id of pendingIds) {
        if (resolvedIds.has(id)) continue;
        // schedule() replaces any existing timer, so a pending slow tick is
        // pulled forward instead of doubled up.
        schedule(id, 250);
      }
    }
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibility);
    }

    return () => {
      cancelled = true;
      for (const t of timers.values()) clearTimeout(t);
      timers.clear();
      for (const c of abortControllers.values()) c.abort();
      abortControllers.clear();
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibility);
      }
    };
    // pendingKey captures the set of pending request IDs; watchGeneration forces
    // a re-register (fresh clock) when resetWatch re-arms an already-pending id.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingKey, markResolved, watchGeneration]);

  // Clean up flash timers on unmount so a fast-mount/unmount cycle (e.g. a
  // test) doesn't leak.
  useEffect(() => {
    const timers = flashTimersRef.current;
    return () => {
      for (const t of timers.values()) clearTimeout(t);
      timers.clear();
    };
  }, []);

  return { justResolved, hasPending, resetWatch };
}

function rowsToPendingIds(rows: ReadonlyArray<WatchableRow>): Set<string> {
  const ids = new Set<string>();
  for (const row of rows) {
    if (row.kind !== "request") continue;
    if (row.status !== "processing" && row.status !== "requested") continue;
    if (!row.requestId) continue;
    ids.add(row.requestId);
  }
  return ids;
}
